import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, FileText, Plus, X, RefreshCw, Pen, Sparkles, Loader2, RotateCcw, ChevronDown, ChevronUp, History, Pause, Play, XCircle, CheckCircle, Upload, Trash2, Download, Users, GripVertical } from 'lucide-react';
import { Button, Input, Label, Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription, Tabs, TabsContent, TabsList, TabsTrigger, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, useToast, Badge, Textarea, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@hiring-intelligence/ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { hiringApi, moduleApi, companyApi, ModuleType } from '@/services/api';
import { ModuleCard } from '@/components/ModuleCard';
import { ContactPicker } from '@/components/crm/layout';
import { CandidateSearch } from '@/components/CandidateSearch';
import { CandidateProcessList } from '@/components/CandidateProcessList';
import { CandidateDiscoveryCard } from '@/components/CandidateDiscoveryCard';
import { CandidateRankingCard } from '@/components/CandidateRankingCard';
import { LocationSelect } from '@/components/LocationSelect';
import { ShareDialog } from '@/components/ShareDialog';
import { MessageSquare, Star, UserCheck } from 'lucide-react';
import { YEARS_OF_EXPERIENCE_LABELS, DIRECT_REPORTS_LABELS, GRADUATE_TYPES_LABELS, LANGUAGES, WORK_TYPES, CONTRACT_TYPE_LABELS, SERVICE_TYPE_OPTIONS, PRIORITY_OPTIONS, DEAL_STAGE_OPTIONS, formatNumber, parseFormattedNumber } from '@hiring-intelligence/shared';
import { useAuthStore } from '@/stores/auth';

// Sortable skill badge for drag-and-drop reordering
function SortableSkill({ id, skill, isEditing, onRemove }: { id: string; skill: string; isEditing: boolean; onRemove: () => void }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

	return (
		<span
			ref={setNodeRef}
			style={style}
			className={`inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm ${isEditing ? 'cursor-grab active:cursor-grabbing' : ''}`}
			{...(isEditing ? { ...attributes, ...listeners } : {})}
		>
			{isEditing && <GripVertical className="h-3 w-3 text-primary/50 shrink-0" />}
			{skill}
			{isEditing && (
				<button onClick={(e) => { e.stopPropagation(); onRemove(); }}>
					<X className="h-3 w-3" />
				</button>
			)}
		</span>
	);
}

// Module configuration for each tab
const TAB_MODULES: Record<string, { type: ModuleType; title: string; description: string }[]> = {
	overview: [
		{ type: 'deal_briefing', title: 'Deal Briefing', description: 'Unified meeting notes, team assessment & KPIs' },
		{ type: 'standard_jd', title: 'Standard Job Description', description: 'Public-facing job description' },
		{ type: 'blind_jd', title: 'Blind Job Description', description: 'Confidential JD with sensitive info hidden' },
		{ type: 'candidate_sourcing', title: 'Candidate Sourcing Information', description: 'Sourcing strategy with tier-based search configurations' }
	],
	sourcing: [],
	feedback_review: []
};

function FeedbackReviewPanel({ hiringRequestId }: { hiringRequestId: string }) {
	const { data, isLoading } = useQuery({
		queryKey: ['feedback-review', hiringRequestId],
		queryFn: () => hiringApi.getFeedbackReview(hiringRequestId)
	});

	if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading feedback...</div>;

	const comments = data?.data?.comments || [];
	const candidateFeedback = data?.data?.candidateFeedback || [];
	const ratings = data?.data?.ratings || [];

	const hasNoData = comments.length === 0 && candidateFeedback.length === 0 && ratings.length === 0;

	if (hasNoData) {
		return (
			<Card>
				<CardContent className="py-12 text-center">
					<MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
					<p className="text-muted-foreground">No feedback received yet from hiring managers.</p>
					<p className="text-sm text-muted-foreground mt-1">Share access links with hiring managers to start collecting feedback.</p>
				</CardContent>
			</Card>
		);
	}

	// Group candidate feedback by candidate
	const feedbackByCandidate: Record<string, { candidate: any; feedbackItems: any[]; ratingItems: any[] }> = {};
	for (const fb of candidateFeedback) {
		const cId = fb.sourcedCandidateId;
		if (!feedbackByCandidate[cId]) {
			feedbackByCandidate[cId] = { candidate: fb.sourcedCandidate, feedbackItems: [], ratingItems: [] };
		}
		feedbackByCandidate[cId].feedbackItems.push(fb);
	}

	// Merge ratings into the same per-candidate grouping
	for (const r of ratings) {
		const cId = r.sourcedCandidateId;
		if (!feedbackByCandidate[cId]) {
			feedbackByCandidate[cId] = { candidate: r.sourcedCandidate, feedbackItems: [], ratingItems: [] };
		}
		feedbackByCandidate[cId].ratingItems.push(r);
	}

	return (
		<div className="space-y-6">
			{/* General Comments from HM */}
			{comments.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<MessageSquare className="h-5 w-5" />
							Hiring Manager Comments ({comments.length})
						</CardTitle>
						<CardDescription>General comments left by hiring managers</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{comments.map((c: any) => (
							<div key={c.id} className="border rounded-lg p-4">
								<div className="flex items-center justify-between mb-2">
									<span className="text-sm font-medium text-muted-foreground">
										{c.accessLink?.email || 'Hiring Manager'}
									</span>
									<span className="text-xs text-muted-foreground">
										{new Date(c.createdAt).toLocaleString()}
									</span>
								</div>
								<p className="text-sm whitespace-pre-wrap">{c.comment}</p>
							</div>
						))}
					</CardContent>
				</Card>
			)}

			{/* Candidate Ratings */}
			{Object.keys(feedbackByCandidate).length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<UserCheck className="h-5 w-5" />
							Candidate Feedback ({candidateFeedback.length + ratings.length})
						</CardTitle>
						<CardDescription>Detailed feedback and ratings per candidate from hiring managers</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{Object.values(feedbackByCandidate).map(({ candidate, feedbackItems, ratingItems }) => (
							<div key={candidate?.id || feedbackItems[0]?.sourcedCandidateId || ratingItems[0]?.sourcedCandidateId} className="border rounded-lg p-4">
								<div className="font-medium mb-1">{candidate?.name || 'Unknown Candidate'}</div>
								{candidate?.currentTitle && (
									<div className="text-sm text-muted-foreground mb-3">
										{candidate.currentTitle}{candidate.currentCompany ? ` at ${candidate.currentCompany}` : ''}
									</div>
								)}

								{/* Ratings */}
								{ratingItems.length > 0 && (
									<div className="mb-3">
										{ratingItems.map((r: any) => (
											<div key={r.id} className="flex flex-wrap items-center gap-2 sm:gap-3 py-2 border-t first:border-t-0">
												<div className="flex items-center gap-1 shrink-0">
													{[1, 2, 3, 4, 5].map((s) => (
														<Star key={s} className={`h-4 w-4 ${s <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
													))}
												</div>
												<span className="text-sm text-muted-foreground">{r.accessLink?.email || 'HM'}</span>
												{r.notes && <span className="text-sm"> &mdash; {r.notes}</span>}
												<span className="text-xs text-muted-foreground sm:ml-auto">{new Date(r.createdAt).toLocaleString()}</span>
											</div>
										))}
									</div>
								)}

								{/* Feedback */}
								{feedbackItems.map((fb: any) => (
									<div key={fb.id} className="py-3 border-t first:border-t-0">
										<div className="flex items-center justify-between mb-1">
											<span className="text-sm font-medium">
												{fb.contact?.name || 'Hiring Manager'}
												{fb.contact?.email && <span className="text-muted-foreground font-normal"> ({fb.contact.email})</span>}
											</span>
											<span className="text-xs text-muted-foreground">{new Date(fb.createdAt).toLocaleString()}</span>
										</div>
										<p className="text-sm whitespace-pre-wrap">{fb.feedback}</p>
									</div>
								))}
							</div>
						))}
					</CardContent>
				</Card>
			)}
		</div>
	);
}

export default function HiringRequestDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const currentUser = useAuthStore((state) => state.user);

	// Tab is deep-linkable via ?tab= (e.g. dashboard job rows land on Candidates).
	const [searchParams, setSearchParams] = useSearchParams();
	const VALID_TABS = ['overview', 'sourcing', 'candidate_process', 'feedback_review'];
	const tabParam = searchParams.get('tab');
	const [activeTab, setActiveTab] = useState(tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'overview');
	const handleTabChange = (tab: string) => {
		setActiveTab(tab);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set('tab', tab);
			return next;
		}, { replace: true });
	};
	const [newSkill, setNewSkill] = useState('');
	const [newResponsibility, setNewResponsibility] = useState('');
	const [isEditHeaderOpen, setIsEditHeaderOpen] = useState(false);
	const [isResearchExpanded, setIsResearchExpanded] = useState(false);
	const [isCriteriaExpanded, setIsCriteriaExpanded] = useState(false);
	const [editHeaderData, setEditHeaderData] = useState<any>({
		roleTitle: '', yearsOfExperience: 'below_2_years', location: '',
		directReports: 'individual_contributor', workType: 'hybrid',
		graduates: '', openToExpat: false,
		contractType: '', urgencyLevel: '',
		serviceType: '', priority: '', feePercentage: '', dealNotes: '', targetDate: '',
		interviewStages: [] as Array<{ name: string; interviewer?: string; feedback?: string }>,
		salaryRange: null as { min: number; max: number; currency: string } | null,
		languages: [] as string[], industry: [] as string[],
		excludedCompanies: [] as string[], preferredCompanies: [] as string[],
		hiringManagerEmails: [] as Array<{ name: string; position?: string; linkedIn?: string }>
	});

	const [isEditingCriteria, setIsEditingCriteria] = useState(false);

	// Edit dialog tag input helpers
	const [editLanguageInput, setEditLanguageInput] = useState('');
	const [editIndustryInput, setEditIndustryInput] = useState('');
	const [editExcludedInput, setEditExcludedInput] = useState('');
	const [editPreferredInput, setEditPreferredInput] = useState('');
	const [editHmNameInput, setEditHmNameInput] = useState('');
	const [editHmPositionInput, setEditHmPositionInput] = useState('');
	const [editHmLinkedInInput, setEditHmLinkedInInput] = useState('');

	// Bulk regenerate state
	const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const [selectedRegenModules, setSelectedRegenModules] = useState<ModuleType[]>([]);
	const [regenerateCriteria, setRegenerateCriteria] = useState(false);
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [regenCriteriaContext, setRegenCriteriaContext] = useState('');
	const [regenModuleContexts, setRegenModuleContexts] = useState<Record<string, string>>({});

	// Criteria context & history state
	const [showCriteriaContext, setShowCriteriaContext] = useState(false);
	const [criteriaContextText, setCriteriaContextText] = useState('');
	const [showCriteriaHistory, setShowCriteriaHistory] = useState(false);
	const [criteriaHistory, setCriteriaHistory] = useState<any[]>([]);
	const [isLoadingCriteriaHistory, setIsLoadingCriteriaHistory] = useState(false);

	// Fetch hiring request
	const { data, isLoading, refetch } = useQuery({
		queryKey: ['hiring-request', id],
		queryFn: () => hiringApi.get(id!),
		refetchInterval: (query) => {
			// Auto-poll every 8s if criteria is still empty (being generated)
			const req = query.state.data?.data;
			const crit = req?.criteria;
			const isEmpty = crit && (!crit.responsibilities || crit.responsibilities.length === 0);
			return isEmpty ? 8000 : false;
		}
	});

	// Fetch modules
	const { data: modulesData, isLoading: modulesLoading, refetch: refetchModules } = useQuery({
		queryKey: ['modules', id],
		queryFn: () => moduleApi.list(id!),
		enabled: !!id,
		refetchInterval: (query) => {
			// Auto-poll every 5s while any module is pending/processing, or generation was recently triggered
			const modules = query.state.data?.data || [];
			const hasInProgress = modules.some((m: any) => m.status === 'pending' || m.status === 'processing');
			if (hasInProgress || isGenerating) return 5000;
			// Also poll if very few modules (backward compat for pre-migration data)
			return modules.length > 0 && modules.length < 3 ? 8000 : false;
		}
	});

	const request = data?.data;
	const criteria = request?.criteria;
	const modules = modulesData?.data || [];

	// Fetch company research (company-level, read-only in HR context)
	const companyId = request?.companyId;
	const { data: companyResearchData, isLoading: companyResearchLoading } = useQuery({
		queryKey: ['company-research', companyId],
		queryFn: () => companyApi.getResearch(companyId),
		enabled: !!companyId,
		refetchInterval: (query) => {
			const research = query.state.data?.data;
			if (research && (research.status === 'pending' || research.status === 'processing')) return 5000;
			return false;
		}
	});
	const companyResearch = companyResearchData?.data;

	// Auto-stop isGenerating when all modules are done (completed or failed)
	useEffect(() => {
		if (!isGenerating || modules.length === 0) return;
		const hasInProgress = modules.some((m: any) => m.status === 'pending' || m.status === 'processing');
		if (!hasInProgress) {
			setIsGenerating(false);
		}
	}, [modules, isGenerating]);

	// Create a map of modules by type for easy lookup
	const modulesByType: Record<string, any> = {};
	modules.forEach((m: any) => {
		modulesByType[m.moduleType] = m;
	});

	const [formData, setFormData] = useState<any>(null);

	// Initialize form data when request loads, and sync when criteria updates from AI generation
	useEffect(() => {
		if (!request) return;

		const isFormEmpty = !formData || (
			formData.responsibilities.length === 0 &&
			formData.mustHaveSkills.length === 0
		);
		const hasCriteriaData = criteria && (
			(criteria.responsibilities && criteria.responsibilities.length > 0) ||
			(criteria.mustHaveSkills && criteria.mustHaveSkills.length > 0)
		);

		// Set form data if not set yet, OR if form is empty but criteria now has data (from AI)
		if (!formData || (isFormEmpty && hasCriteriaData)) {
			setFormData({
				responsibilities: criteria?.responsibilities || [],
				mustHaveSkills: criteria?.mustHaveSkills || [],
				niceToHaveSkills: criteria?.niceToHaveSkills || [],
				teamCulture: criteria?.teamCulture || [],
				successMetrics: criteria?.successMetrics || []
			});
		}

		setEditHeaderData({
			roleTitle: request.roleTitle,
			yearsOfExperience: request.yearsOfExperience || 'below_2_years',
			location: request.location || '',
			directReports: request.directReports || 'individual_contributor',
			workType: request.workType || 'hybrid',
			graduates: request.graduates || '',
			openToExpat: request.openToExpat ?? false,
			contractType: request.contractType || '',
			urgencyLevel: request.urgencyLevel || '',
			serviceType: request.serviceType || '',
			priority: request.priority || '',
			feePercentage: request.feePercentage != null ? String(request.feePercentage) : '',
			dealNotes: request.dealNotes || '',
			targetDate: request.targetDate ? new Date(request.targetDate).toISOString().slice(0, 10) : '',
			interviewStages: (request.interviewStages || []).map((s: any) => ({ name: s.name || '', interviewer: s.interviewer || '', feedback: s.feedback || '' })),
			salaryRange: request.salaryRange || null,
			languages: request.languages || [],
			industry: request.industry || [],
			excludedCompanies: request.excludedCompanies || [],
			preferredCompanies: request.preferredCompanies || [],
			hiringManagerEmails: request.hiringManagerEmails || []
		});
	}, [request, criteria]);

	const updateCriteriaMutation = useMutation({
		mutationFn: (data: any) => hiringApi.updateCriteria(id!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
			setIsEditingCriteria(false);
			toast({ title: 'Saved', description: 'Criteria updated successfully' });
		}
	});

	const updateHeaderMutation = useMutation({
		mutationFn: (data: any) => hiringApi.update(id!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
			setIsEditHeaderOpen(false);
			toast({ title: 'Updated', description: 'Request details updated' });
		}
	});

	// Closing stages (placed/lost) get a confirmation dialog; lost also captures a reason.
	const [pendingDealStatus, setPendingDealStatus] = useState<string | null>(null);
	const [lostReasonText, setLostReasonText] = useState('');

	const updateDealStatusMutation = useMutation({
		mutationFn: ({ dealStatus, lostReason }: { dealStatus: string; lostReason?: string }) =>
			hiringApi.updateDealStatus(id!, dealStatus, lostReason),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
			setPendingDealStatus(null);
			setLostReasonText('');
			toast({ title: 'Pipeline updated' });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const handleDealStatusChange = (value: string) => {
		if (value === 'placed' || value === 'lost') {
			setPendingDealStatus(value);
		} else {
			updateDealStatusMutation.mutate({ dealStatus: value });
		}
	};

	const generateCriteriaMutation = useMutation({
		mutationFn: (additionalContext?: string) => hiringApi.generateCriteria(id!, additionalContext),
		onSuccess: () => {
			toast({ title: 'Generating', description: 'Analyzing role details to fill criteria...' });
			setShowCriteriaContext(false);
			setCriteriaContextText('');
			// Poll or wait - strictly for demo simplify by waiting 5s then reload
			setTimeout(() => {
				// Reset to empty so useEffect re-fills from fresh criteria (don't set null — that triggers Loading screen)
				setFormData({
					responsibilities: [],
					mustHaveSkills: [],
					niceToHaveSkills: [],
					teamCulture: [],
					successMetrics: []
				});
				queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
			}, 5000);
		},
		onError: () => {
			toast({ title: 'Error', description: 'Failed to start generation', variant: 'destructive' });
		}
	});

	const fetchCriteriaHistory = async () => {
		setIsLoadingCriteriaHistory(true);
		try {
			const res = await hiringApi.getCriteriaHistory(id!);
			setCriteriaHistory(res.data || []);
		} catch {
			toast({ title: 'Error', description: 'Failed to load criteria history', variant: 'destructive' });
		} finally {
			setIsLoadingCriteriaHistory(false);
		}
	};

	const handleRestoreCriteria = async (version: number) => {
		try {
			await hiringApi.restoreCriteria(id!, version);
			setShowCriteriaHistory(false);
			toast({ title: 'Restored', description: `Criteria restored to v${version}` });
			queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
			setFormData(null);
		} catch {
			toast({ title: 'Error', description: 'Failed to restore criteria', variant: 'destructive' });
		}
	};

	const updateStatusMutation = useMutation({
		mutationFn: (status: 'draft' | 'cancelled' | 'completed' | 'resume') => hiringApi.updateStatus(id!, status),
		onSuccess: (_, status) => {
			queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
			queryClient.invalidateQueries({ queryKey: ['hiring-requests'] });
			const labels: Record<string, string> = { draft: 'Draft', cancelled: 'Cancelled', completed: 'Completed', resume: 'Resumed' };
			toast({ title: 'Status Updated', description: `Hiring request ${status === 'resume' ? 'resumed' : `set to ${labels[status]}`}` });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const handleGenerate = async (moduleType: ModuleType, additionalContext?: string, meetingNotes?: string) => {
		await moduleApi.generate(id!, moduleType, additionalContext, meetingNotes);
		toast({
			title: 'Generation Started',
			description: additionalContext
				? `${moduleType} regeneration with context has been queued.`
				: `${moduleType} generation has been queued.`
		});
		// Refresh after a delay
		setTimeout(() => refetchModules(), 3000);
	};

	const handleSave = async (moduleType: ModuleType, data: { content?: Record<string, unknown>; readableContent?: string }) => {
		await moduleApi.update(id!, moduleType, data);
		refetchModules();
	};

	const handleAddItem = (field: string, value: string, setter: (v: string) => void) => {
		if (value.trim()) {
			setFormData({ ...formData, [field]: [...formData[field], value.trim()] });
			setter('');
		}
	};

	const handleRemoveItem = (field: string, index: number) => {
		setFormData({
			...formData,
			[field]: formData[field].filter((_: any, i: number) => i !== index)
		});
	};

	// DnD sensors for skill reordering
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);

	const handleSkillDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = formData.mustHaveSkills.findIndex((_: string, i: number) => `skill-${i}` === active.id);
		const newIndex = formData.mustHaveSkills.findIndex((_: string, i: number) => `skill-${i}` === over.id);
		if (oldIndex !== -1 && newIndex !== -1) {
			setFormData({ ...formData, mustHaveSkills: arrayMove(formData.mustHaveSkills, oldIndex, newIndex) });
		}
	}, [formData]);

	// Flatten all modules from TAB_MODULES for the regenerate dialog
	const allRegeneratableModules = Object.values(TAB_MODULES).flat();

	const handleToggleRegenModule = (moduleType: ModuleType) => {
		setSelectedRegenModules((prev) =>
			prev.includes(moduleType)
				? prev.filter((m) => m !== moduleType)
				: [...prev, moduleType]
		);
	};

	const handleSelectAllRegen = () => {
		if (selectedRegenModules.length === allRegeneratableModules.length) {
			setSelectedRegenModules([]);
			setRegenerateCriteria(false);
		} else {
			setSelectedRegenModules(allRegeneratableModules.map((m) => m.type));
			setRegenerateCriteria(true);
		}
	};

	const handleBulkRegenerate = async () => {
		setIsRegenerating(true);
		setIsGenerating(true);
		try {
			// Build per-module contexts (only for selected modules that have context)
			const activeModuleContexts: Record<string, string> = {};
			for (const mt of selectedRegenModules) {
				if (regenModuleContexts[mt]?.trim()) {
					activeModuleContexts[mt] = regenModuleContexts[mt].trim();
				}
			}
			const activeCriteriaContext = regenerateCriteria ? regenCriteriaContext.trim() || undefined : undefined;

			// Single orchestrated call: criteria completes first (if selected), then phased modules
			await hiringApi.generateModules(
				id!,
				selectedRegenModules,
				regenerateCriteria,
				undefined,
				activeCriteriaContext,
				Object.keys(activeModuleContexts).length > 0 ? activeModuleContexts : undefined
			);

			const totalCount = selectedRegenModules.length + (regenerateCriteria ? 1 : 0);
			toast({
				title: 'Regeneration Started',
				description: `${totalCount} item(s) queued for regeneration.`
			});

			setRegenerateDialogOpen(false);
			setSelectedRegenModules([]);
			setRegenerateCriteria(false);

			// Refresh immediately and let polling handle the rest
			setTimeout(() => {
				refetch();
				refetchModules();
				setFormData(null);
			}, 2000);

			// Stop isGenerating after a generous timeout (pipeline takes ~5-10 min for all modules)
			setTimeout(() => setIsGenerating(false), 10 * 60 * 1000);
		} catch (error) {
			toast({ title: 'Error', description: 'Failed to start regeneration', variant: 'destructive' });
			setIsGenerating(false);
		} finally {
			setIsRegenerating(false);
		}
	};

	if (isLoading || !formData) {
		return <div className="p-4 md:p-8 text-center">Loading...</div>;
	}

	return (
		<div className="p-4 md:p-8">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
				<div className="flex items-center gap-2 sm:gap-4 min-w-0">
					<Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/hiring-requests')}>
						<ArrowLeft className="h-5 w-5" />
					</Button>
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<h1 className="text-xl sm:text-2xl font-bold truncate">{request.roleTitle}</h1>
							<Dialog open={isEditHeaderOpen} onOpenChange={setIsEditHeaderOpen}>
								<DialogTrigger asChild>
									<Button variant="ghost" size="icon" className="h-6 w-6">
										<Pen className="h-3 w-3" />
									</Button>
								</DialogTrigger>
								<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
									<DialogHeader>
										<DialogTitle>Edit Request Details</DialogTitle>
										<DialogDescription>Update the core details of this hiring request.</DialogDescription>
									</DialogHeader>
									<div className="grid gap-4 py-4">
										{/* Row 1: Role Title, Years of Experience */}
										<div className="grid gap-4 md:grid-cols-2">
											<div className="grid gap-2">
												<Label>Role Title</Label>
												<Input
													value={editHeaderData.roleTitle}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, roleTitle: e.currentTarget.value })}
												/>
											</div>
											<div className="grid gap-2">
												<Label>Years of Experience</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.yearsOfExperience}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, yearsOfExperience: e.currentTarget.value })}
												>
													{Object.entries(YEARS_OF_EXPERIENCE_LABELS).map(([value, label]) => (
														<option key={value} value={value}>{label}</option>
													))}
												</select>
											</div>
										</div>
										
										{/* Row 2: Direct Reports, Work Type, Location */}
										<div className="grid gap-4 md:grid-cols-3">
											<div className="grid gap-2">
												<Label>Direct Reports</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.directReports}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, directReports: e.currentTarget.value })}
												>
													{Object.entries(DIRECT_REPORTS_LABELS).map(([value, label]) => (
														<option key={value} value={value}>{label}</option>
													))}
												</select>
											</div>
											<div className="grid gap-2">
												<Label>Work Type</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.workType}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, workType: e.currentTarget.value })}
												>
													{Object.values(WORK_TYPES).map(wt => (
														<option key={wt} value={wt}>{wt.charAt(0).toUpperCase() + wt.slice(1)}</option>
													))}
												</select>
											</div>
											<div className="grid gap-2">
												<Label>Location</Label>
												<LocationSelect
													value={editHeaderData.location}
													onChange={(val) => setEditHeaderData({ ...editHeaderData, location: val })}
												/>
											</div>
										</div>
										
										{/* Row 3: Graduates, Open to Expat */}
										<div className="grid gap-4 md:grid-cols-2">
											<div className="grid gap-2">
												<Label>Graduates</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.graduates}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, graduates: e.currentTarget.value })}
												>
													<option value="">Not specified</option>
													{Object.entries(GRADUATE_TYPES_LABELS).map(([value, label]) => (
														<option key={value} value={value}>{label}</option>
													))}
												</select>
											</div>
											<div className="flex items-end">
												<label className="flex items-center gap-2 h-9">
													<input
														type="checkbox"
														checked={editHeaderData.openToExpat}
														onChange={(e) => setEditHeaderData({ ...editHeaderData, openToExpat: e.target.checked })}
														className="rounded"
													/>
													<span className="text-sm font-medium">Open to Expat</span>
												</label>
											</div>
										</div>
										
										{/* Row 4: Contract Type, Urgency */}
										<div className="grid gap-4 md:grid-cols-2">
											<div className="grid gap-2">
												<Label>Contract Type</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.contractType}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, contractType: e.currentTarget.value })}
												>
													<option value="">Select...</option>
													{Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
														<option key={value} value={value}>{label as string}</option>
													))}
												</select>
											</div>
											<div className="grid gap-2">
												<Label>Urgency</Label>
												<Input
													value={editHeaderData.urgencyLevel}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, urgencyLevel: e.currentTarget.value })}
													placeholder="e.g. Maximum Join Date August/September (2-3 months notice period acceptable)"
												/>
											</div>
										</div>

										{/* Row 5: Service Type, Priority, Fee %, Target Start (deal parity — completable later) */}
										<div className="grid gap-4 md:grid-cols-4">
											<div className="grid gap-2">
												<Label>Service Type</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.serviceType}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, serviceType: e.currentTarget.value })}
												>
													<option value="">Select...</option>
													{SERVICE_TYPE_OPTIONS.map((o) => (
														<option key={o.value} value={o.value}>{o.label}</option>
													))}
												</select>
											</div>
											<div className="grid gap-2">
												<Label>Priority</Label>
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
													value={editHeaderData.priority}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, priority: e.currentTarget.value })}
												>
													<option value="">Select...</option>
													{PRIORITY_OPTIONS.map((o) => (
														<option key={o.value} value={o.value}>{o.label}</option>
													))}
												</select>
											</div>
											<div className="grid gap-2">
												<Label>Fee %</Label>
												<Input
													type="number"
													min={0}
													max={100}
													step="0.1"
													value={editHeaderData.feePercentage}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, feePercentage: e.currentTarget.value })}
													placeholder="e.g. 20"
												/>
											</div>
											<div className="grid gap-2">
												<Label>Target Start Date</Label>
												<Input
													type="date"
													value={editHeaderData.targetDate}
													onChange={(e) => setEditHeaderData({ ...editHeaderData, targetDate: e.currentTarget.value })}
												/>
											</div>
										</div>

										{/* Deal description (deal parity) */}
										<div className="grid gap-2">
											<Label>Deal Description</Label>
											<Textarea
												rows={3}
												placeholder="Context about this opportunity…"
												value={editHeaderData.dealNotes}
												onChange={(e) => setEditHeaderData({ ...editHeaderData, dealNotes: e.currentTarget.value })}
											/>
										</div>

										{/* Salary Range */}
										<div>
											<Label>Salary Range (Nett)</Label>
											<div className="grid gap-2 md:grid-cols-3 mt-1">
												<Input
													placeholder="Min"
													value={formatNumber(editHeaderData.salaryRange?.min)}
													onChange={(e) => {
														const val = parseFormattedNumber(e.target.value);
														setEditHeaderData({
															...editHeaderData,
															salaryRange: { min: val, max: editHeaderData.salaryRange?.max ?? 0, currency: editHeaderData.salaryRange?.currency ?? 'IDR' }
														});
													}}
												/>
												<Input
													placeholder="Max"
													value={formatNumber(editHeaderData.salaryRange?.max)}
													onChange={(e) => {
														const val = parseFormattedNumber(e.target.value);
														setEditHeaderData({
															...editHeaderData,
															salaryRange: { min: editHeaderData.salaryRange?.min ?? 0, max: val, currency: editHeaderData.salaryRange?.currency ?? 'IDR' }
														});
													}}
												/>
												<Input
													placeholder="Currency (e.g. IDR)"
													value={editHeaderData.salaryRange?.currency ?? ''}
													onChange={(e) => setEditHeaderData({
														...editHeaderData,
														salaryRange: { min: editHeaderData.salaryRange?.min ?? 0, max: editHeaderData.salaryRange?.max ?? 0, currency: e.target.value }
													})}
												/>
											</div>
										</div>

										{/* Interview Stages (deal parity — completable later) */}
										<div>
											<div className="flex items-center justify-between">
												<Label>Interview Stages</Label>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => setEditHeaderData({ ...editHeaderData, interviewStages: [...(editHeaderData.interviewStages || []), { name: '', interviewer: '', feedback: '' }] })}
												>
													Add Stage
												</Button>
											</div>
											{(editHeaderData.interviewStages || []).length === 0 && (
												<p className="text-xs text-muted-foreground mt-1">No stages yet.</p>
											)}
											<div className="space-y-2 mt-2">
												{(editHeaderData.interviewStages || []).map((s: { name: string; interviewer?: string; feedback?: string }, idx: number) => (
													<div key={idx} className="rounded-md border border-input p-2 space-y-2">
														<div className="flex gap-2">
															<Input
																placeholder="Stage name"
																value={s.name}
																onChange={(e) => setEditHeaderData({ ...editHeaderData, interviewStages: editHeaderData.interviewStages.map((x: any, i: number) => i === idx ? { ...x, name: e.target.value } : x) })}
															/>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() => setEditHeaderData({ ...editHeaderData, interviewStages: editHeaderData.interviewStages.filter((_: any, i: number) => i !== idx) })}
															>
																<X className="h-4 w-4" />
															</Button>
														</div>
														<Input
															placeholder="Interviewer (optional)"
															value={s.interviewer || ''}
															onChange={(e) => setEditHeaderData({ ...editHeaderData, interviewStages: editHeaderData.interviewStages.map((x: any, i: number) => i === idx ? { ...x, interviewer: e.target.value } : x) })}
														/>
														<Textarea
															placeholder="Feedback (optional)"
															rows={2}
															value={s.feedback || ''}
															onChange={(e) => setEditHeaderData({ ...editHeaderData, interviewStages: editHeaderData.interviewStages.map((x: any, i: number) => i === idx ? { ...x, feedback: e.target.value } : x) })}
														/>
													</div>
												))}
											</div>
										</div>

										{/* Languages */}
										<div>
											<Label>Languages</Label>
											<div className="flex flex-wrap gap-1 my-1">
												{editHeaderData.languages.map((lang: string, i: number) => (
													<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
														{lang}
														<button type="button" onClick={() => setEditHeaderData({ ...editHeaderData, languages: editHeaderData.languages.filter((_: string, idx: number) => idx !== i) })}>
															<X className="h-3 w-3" />
														</button>
													</span>
												))}
											</div>
											<div className="flex gap-2">
												<select
													className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm flex-1"
													value=""
													onChange={(e) => {
														if (e.target.value && !editHeaderData.languages.includes(e.target.value)) {
															setEditHeaderData({ ...editHeaderData, languages: [...editHeaderData.languages, e.target.value] });
														}
													}}
												>
													<option value="">Select language...</option>
													{LANGUAGES.filter((l: string) => !editHeaderData.languages.includes(l)).map((l: string) => (
														<option key={l} value={l}>{l}</option>
													))}
												</select>
												<Input
													placeholder="Custom language"
													className="flex-1"
													value={editLanguageInput}
													onChange={(e) => setEditLanguageInput(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' && editLanguageInput.trim() && !editHeaderData.languages.includes(editLanguageInput.trim())) {
															e.preventDefault();
															setEditHeaderData({ ...editHeaderData, languages: [...editHeaderData.languages, editLanguageInput.trim()] });
															setEditLanguageInput('');
														}
													}}
												/>
											</div>
										</div>
										
										{/* Industry */}
										<div>
											<Label>Industry</Label>
											<div className="flex flex-wrap gap-1 my-1">
												{editHeaderData.industry.map((ind: string, i: number) => (
													<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
														{ind}
														<button type="button" onClick={() => setEditHeaderData({ ...editHeaderData, industry: editHeaderData.industry.filter((_: string, idx: number) => idx !== i) })}>
															<X className="h-3 w-3" />
														</button>
													</span>
												))}
											</div>
											<Input
												placeholder="Type industry and press Enter"
												value={editIndustryInput}
												onChange={(e) => setEditIndustryInput(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' && editIndustryInput.trim() && !editHeaderData.industry.includes(editIndustryInput.trim())) {
														e.preventDefault();
														setEditHeaderData({ ...editHeaderData, industry: [...editHeaderData.industry, editIndustryInput.trim()] });
														setEditIndustryInput('');
													}
												}}
											/>
										</div>
										
										{/* Excluded & Preferred Companies */}
										<div className="grid gap-4 md:grid-cols-2">
											<div>
												<Label>Excluded Companies</Label>
												<div className="flex flex-wrap gap-1 my-1">
													{editHeaderData.excludedCompanies.map((c: string, i: number) => (
														<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
															{c}
															<button type="button" onClick={() => setEditHeaderData({ ...editHeaderData, excludedCompanies: editHeaderData.excludedCompanies.filter((_: string, idx: number) => idx !== i) })}>
																<X className="h-3 w-3" />
															</button>
														</span>
													))}
												</div>
												<Input
													placeholder="Type company name and press Enter"
													value={editExcludedInput}
													onChange={(e) => setEditExcludedInput(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' && editExcludedInput.trim()) {
															e.preventDefault();
															setEditHeaderData({ ...editHeaderData, excludedCompanies: [...editHeaderData.excludedCompanies, editExcludedInput.trim()] });
															setEditExcludedInput('');
														}
													}}
												/>
											</div>
											<div>
												<Label>Preferred Companies</Label>
												<div className="flex flex-wrap gap-1 my-1">
													{editHeaderData.preferredCompanies.map((c: string, i: number) => (
														<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
															{c}
															<button type="button" onClick={() => setEditHeaderData({ ...editHeaderData, preferredCompanies: editHeaderData.preferredCompanies.filter((_: string, idx: number) => idx !== i) })}>
																<X className="h-3 w-3" />
															</button>
														</span>
													))}
												</div>
												<Input
													placeholder="Type company name and press Enter"
													value={editPreferredInput}
													onChange={(e) => setEditPreferredInput(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' && editPreferredInput.trim()) {
															e.preventDefault();
															setEditHeaderData({ ...editHeaderData, preferredCompanies: [...editHeaderData.preferredCompanies, editPreferredInput.trim()] });
															setEditPreferredInput('');
														}
													}}
												/>
											</div>
										</div>
										
										{/* Hiring Managers */}
										<div>
											<div className="flex items-center justify-between gap-2">
												<Label>Hiring Managers</Label>
												<ContactPicker
													companyId={companyId}
													label="Select existing"
													onSelect={(c) => {
														if (!c.name || editHeaderData.hiringManagerEmails.some((hm: any) => hm.name === c.name)) return;
														setEditHeaderData({ ...editHeaderData, hiringManagerEmails: [...editHeaderData.hiringManagerEmails, { name: c.name, position: c.title || undefined }] });
													}}
												/>
											</div>
											<div className="space-y-1 my-1">
												{editHeaderData.hiringManagerEmails.map((hm: any, i: number) => (
													<div key={i} className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-1.5">
														<span className="text-sm flex-1">
															{hm.name}{hm.position ? ` — ${hm.position}` : ''}{hm.linkedIn ? ` • ${hm.linkedIn}` : ''}
														</span>
														<button type="button" onClick={() => setEditHeaderData({ ...editHeaderData, hiringManagerEmails: editHeaderData.hiringManagerEmails.filter((_: any, idx: number) => idx !== i) })}>
															<X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
														</button>
													</div>
												))}
											</div>
											<div className="flex flex-col sm:flex-row gap-2">
												<Input
													placeholder="Name *"
													value={editHmNameInput}
													onChange={(e) => setEditHmNameInput(e.target.value)}
													className="flex-1"
												/>
												<Input
													placeholder="Position (optional)"
													value={editHmPositionInput}
													onChange={(e) => setEditHmPositionInput(e.target.value)}
													className="flex-1"
												/>
												<Input
													placeholder="LinkedIn URL (optional)"
													value={editHmLinkedInInput}
													onChange={(e) => setEditHmLinkedInInput(e.target.value)}
													className="flex-1"
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															e.preventDefault();
															const name = editHmNameInput.trim();
															if (!name) return;
															setEditHeaderData({ ...editHeaderData, hiringManagerEmails: [...editHeaderData.hiringManagerEmails, { name, position: editHmPositionInput.trim() || undefined, linkedIn: editHmLinkedInInput.trim() || undefined }] });
															setEditHmNameInput('');
															setEditHmPositionInput('');
															setEditHmLinkedInInput('');
														}
													}}
												/>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => {
														const name = editHmNameInput.trim();
														if (!name) return;
														setEditHeaderData({ ...editHeaderData, hiringManagerEmails: [...editHeaderData.hiringManagerEmails, { name, position: editHmPositionInput.trim() || undefined, linkedIn: editHmLinkedInInput.trim() || undefined }] });
														setEditHmNameInput('');
														setEditHmPositionInput('');
														setEditHmLinkedInInput('');
													}}
												>
													Add
												</Button>
											</div>
											<p className="text-xs text-muted-foreground mt-1">Name is required.</p>
										</div>
									</div>
									<DialogFooter>
										<Button variant="outline" onClick={() => setIsEditHeaderOpen(false)}>Cancel</Button>
										<Button
											onClick={() => updateHeaderMutation.mutate({
												...editHeaderData,
												serviceType: editHeaderData.serviceType || null,
												priority: editHeaderData.priority || null,
												feePercentage: editHeaderData.feePercentage ? Number(editHeaderData.feePercentage) : null,
												dealNotes: editHeaderData.dealNotes || null,
												targetDate: editHeaderData.targetDate ? new Date(editHeaderData.targetDate).toISOString() : undefined,
												interviewStages: (editHeaderData.interviewStages || [])
													.filter((s: { name: string }) => s.name && s.name.trim())
													.map((s: { name: string; interviewer?: string; feedback?: string }) => ({
														name: s.name.trim(),
														interviewer: s.interviewer?.trim() || undefined,
														feedback: s.feedback?.trim() || undefined
													}))
											})}
											disabled={updateHeaderMutation.isPending}
										>
											Save Changes
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>
						</div>
						<p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5 flex-wrap">
							<span className="font-medium">Pipeline:</span>
							<select
								className="h-6 rounded-md border border-input bg-transparent px-1.5 text-xs"
								value={request.dealStatus || 'lead'}
								onChange={(e) => handleDealStatusChange(e.target.value)}
								disabled={updateDealStatusMutation.isPending}
							>
								{DEAL_STAGE_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
							{request.dealId && (
								<a href={`/deals/${request.dealId}`} className="inline-flex items-center gap-1 text-primary hover:underline">
									Deal detail →
								</a>
							)}
							{request.dealCode && <span className="text-muted-foreground/80">· {request.dealCode}</span>}
						</p>
						<Dialog open={pendingDealStatus !== null} onOpenChange={(o) => { if (!o) { setPendingDealStatus(null); setLostReasonText(''); } }}>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>{pendingDealStatus === 'lost' ? 'Mark this lead as Lost?' : 'Mark this lead as Placed?'}</DialogTitle>
									<DialogDescription>
										{pendingDealStatus === 'lost'
											? 'The linked deal will be closed as failed. You can optionally record why.'
											: 'The linked deal will be closed as won (placed).'}
									</DialogDescription>
								</DialogHeader>
								{pendingDealStatus === 'lost' && (
									<Textarea
										rows={3}
										placeholder="Lost reason (optional)…"
										value={lostReasonText}
										onChange={(e) => setLostReasonText(e.target.value)}
									/>
								)}
								<DialogFooter>
									<Button variant="outline" onClick={() => { setPendingDealStatus(null); setLostReasonText(''); }}>Cancel</Button>
									<Button
										variant={pendingDealStatus === 'lost' ? 'destructive' : 'default'}
										disabled={updateDealStatusMutation.isPending}
										onClick={() => updateDealStatusMutation.mutate({
											dealStatus: pendingDealStatus!,
											lostReason: pendingDealStatus === 'lost' ? (lostReasonText.trim() || undefined) : undefined
										})}
									>
										{pendingDealStatus === 'lost' ? 'Mark as Lost' : 'Mark as Placed'}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
						{(request.serviceType || request.priority || request.feePercentage != null) && (
							<p className="text-xs text-muted-foreground mb-1">
								{[
									request.serviceType && (SERVICE_TYPE_OPTIONS.find((o) => o.value === request.serviceType)?.label || request.serviceType),
									request.priority && `Priority: ${PRIORITY_OPTIONS.find((o) => o.value === request.priority)?.label || request.priority}`,
									request.feePercentage != null && `Fee: ${Number(request.feePercentage)}%`
								].filter(Boolean).join(' • ')}
							</p>
						)}
						<p className="text-muted-foreground flex items-center gap-2 flex-wrap text-sm">
							{request.company?.name || 'No company'} • {YEARS_OF_EXPERIENCE_LABELS[request.yearsOfExperience] || request.yearsOfExperience} • {request.location || 'No location'}
							<span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${{
								draft: 'bg-gray-100 text-gray-700',
								gathering_criteria: 'bg-blue-100 text-blue-700',
								criteria_complete: 'bg-indigo-100 text-indigo-700',
								jd_generated: 'bg-orange-100 text-orange-700',
								pending_feedback: 'bg-purple-100 text-purple-700',
								hiring: 'bg-teal-100 text-teal-700',
								completed: 'bg-green-100 text-green-700',
								cancelled: 'bg-red-100 text-red-700'
							}[request.status as string] || 'bg-gray-100 text-gray-700'}`}>
								{{
									draft: 'Draft', gathering_criteria: 'Gathering Criteria', criteria_complete: 'Ready for Sourcing',
									jd_generated: 'JD Generated', pending_feedback: 'Pending Feedback', hiring: 'Hiring',
									completed: 'Completed', cancelled: 'Cancelled'
								}[request.status as string] || request.status}
							</span>
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2 shrink-0">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setShareDialogOpen(true)}
					>
						<Users className="mr-2 h-4 w-4" />
						<span className="hidden sm:inline">Share</span>
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => { refetch(); refetchModules(); }}
					>
						<RefreshCw className="mr-2 h-4 w-4" />
						<span className="hidden sm:inline">Refresh</span>
					</Button>
					{request.status !== 'completed' && <DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm">
								Actions
								<ChevronDown className="ml-2 h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{(request.status === 'draft' || request.status === 'cancelled') && (
								<DropdownMenuItem
									className="text-blue-600 cursor-pointer"
									onClick={() => updateStatusMutation.mutate('resume')}
									disabled={updateStatusMutation.isPending}
								>
									<Play className="mr-2 h-4 w-4" />
									Resume
								</DropdownMenuItem>
							)}
							{request.status !== 'draft' && request.status !== 'cancelled' && request.status !== 'completed' && (
								<DropdownMenuItem
									className="cursor-pointer"
									onClick={() => updateStatusMutation.mutate('draft')}
									disabled={updateStatusMutation.isPending}
								>
									<Pause className="mr-2 h-4 w-4" />
									Set to Draft
								</DropdownMenuItem>
							)}
							{(request.status === 'hiring' || request.status === 'pending_feedback') && (
								<DropdownMenuItem
									className="text-green-600 cursor-pointer"
									onClick={() => updateStatusMutation.mutate('completed')}
									disabled={updateStatusMutation.isPending}
								>
									<CheckCircle className="mr-2 h-4 w-4" />
									Complete
								</DropdownMenuItem>
							)}
							{request.status !== 'completed' && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										className="cursor-pointer"
										onClick={() => {
											setRegenCriteriaContext(criteria?.additionalContext || '');
											const prefilled: Record<string, string> = {};
											for (const config of allRegeneratableModules) {
												const mod = modulesByType[config.type];
												if (mod?.additionalContext) prefilled[config.type] = mod.additionalContext;
											}
											setRegenModuleContexts(prefilled);
											setRegenerateDialogOpen(true);
										}}
									>
										<RotateCcw className="mr-2 h-4 w-4" />
										Regenerate All Tasks
									</DropdownMenuItem>
								</>
							)}
							{request.status !== 'cancelled' && request.status !== 'completed' && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										className="text-red-600 cursor-pointer"
										onClick={() => {
											if (window.confirm('Cancel this hiring request? This action can be undone by setting it back to Draft.')) {
												updateStatusMutation.mutate('cancelled');
											}
										}}
										disabled={updateStatusMutation.isPending}
									>
										<XCircle className="mr-2 h-4 w-4" />
										Cancel Request
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>}
				</div>
			</div>

			{/* Bulk Regenerate Dialog */}
			<Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>Regenerate Modules</DialogTitle>
						<DialogDescription>
							Select which items to regenerate. This will <strong>overwrite</strong> the current content of selected modules.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						{/* Select All toggle */}
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium">Select items to regenerate</span>
							<Button variant="ghost" size="sm" onClick={handleSelectAllRegen}>
								{selectedRegenModules.length === allRegeneratableModules.length && regenerateCriteria ? 'Deselect All' : 'Select All'}
							</Button>
						</div>

						{/* Hiring Criteria - special item */}
						<div className={`rounded-lg border transition-colors ${regenerateCriteria ? 'bg-primary/10 border-primary/30' : ''}`}>
							<label className="flex items-center gap-3 p-3 cursor-pointer">
								<input
									type="checkbox"
									checked={regenerateCriteria}
									onChange={() => setRegenerateCriteria(!regenerateCriteria)}
									className="rounded"
								/>
								<div className="flex-1">
									<span className="text-sm font-medium">Hiring Criteria</span>
									<span className="text-xs text-muted-foreground ml-2">(generates first)</span>
								</div>
								{criteria && ((criteria.responsibilities?.length > 0) || (criteria.mustHaveSkills?.length > 0)) && (
									<Badge variant="outline" className="text-xs">Has data</Badge>
								)}
							</label>
							{regenerateCriteria && (
								<div className="px-3 pb-3">
									<Textarea
										placeholder="Additional context for criteria generation... (optional)"
										value={regenCriteriaContext}
										onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRegenCriteriaContext(e.target.value)}
										rows={2}
										className="text-xs"
									/>
								</div>
							)}
						</div>

						{/* Divider */}
						<div className="border-t" />

						{/* Module checkboxes */}
						<div className="grid gap-2 max-h-[320px] overflow-y-auto">
							{allRegeneratableModules.map((config) => {
								const existing = modulesByType[config.type];
								const isSelected = selectedRegenModules.includes(config.type);
								return (
									<div
										key={config.type}
										className={`rounded transition-colors ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
									>
										<label className="flex items-center gap-3 p-2 cursor-pointer">
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => handleToggleRegenModule(config.type)}
												className="rounded"
											/>
											<span className="text-sm flex-1">{config.title}</span>
											{existing && (
												<Badge variant="outline" className="text-xs">v{existing.version}</Badge>
											)}
										</label>
										{isSelected && (
											<div className="px-2 pb-2">
												<Textarea
													placeholder={`Additional context for ${config.title}... (optional)`}
													value={regenModuleContexts[config.type] || ''}
													onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
														setRegenModuleContexts(prev => ({ ...prev, [config.type]: e.target.value }))
													}
													rows={2}
													className="text-xs"
												/>
											</div>
										)}
									</div>
								);
							})}
						</div>

						{/* Divider */}
						<div className="border-t" />

						{/* Context Files */}
						<div className="space-y-2">
							<span className="text-sm font-medium">Context Files</span>
							<p className="text-xs text-muted-foreground">Reference documents included as additional context during generation.</p>
							{(request?.contextFiles as any[] || []).length > 0 && (
								<div className="space-y-1.5">
									{(request!.contextFiles as any[]).map((f: any, i: number) => (
										<div key={i} className="flex items-center justify-between p-2 border rounded-lg">
											<div className="flex items-center gap-2 min-w-0">
												<FileText className="h-4 w-4 text-muted-foreground shrink-0" />
												<div className="min-w-0">
													<p className="text-sm font-medium truncate">{f.name}</p>
													<p className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</p>
												</div>
											</div>
											<div className="flex items-center gap-1">
												<a href={f.url} target="_blank" rel="noopener noreferrer">
													<Button variant="ghost" size="icon" className="h-7 w-7">
														<Download className="h-3.5 w-3.5" />
													</Button>
												</a>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-destructive hover:text-destructive"
													onClick={async () => {
														try {
															await hiringApi.deleteContextFile(id!, f.gcsPath);
															refetch();
															toast({ title: 'Deleted', description: `${f.name} removed` });
														} catch {
															toast({ title: 'Error', description: 'Failed to delete file', variant: 'destructive' });
														}
													}}
												>
													<Trash2 className="h-3.5 w-3.5" />
												</Button>
											</div>
										</div>
									))}
								</div>
							)}
							<label className="flex items-center gap-2 cursor-pointer border border-dashed rounded-lg p-2.5 hover:bg-muted/50 transition-colors">
								<Upload className="h-4 w-4 text-muted-foreground" />
								<span className="text-xs text-muted-foreground">Upload file (PDF, DOC, DOCX, TXT — max 10MB)</span>
								<input
									type="file"
									className="hidden"
									accept=".pdf,.doc,.docx,.txt"
									onChange={async (e) => {
										const file = e.target.files?.[0];
										if (!file) return;
										if (file.size > 10 * 1024 * 1024) {
											toast({ title: 'Error', description: 'File too large (max 10MB)', variant: 'destructive' });
											return;
										}
										try {
											await hiringApi.uploadContextFile(id!, file);
											refetch();
											toast({ title: 'Uploaded', description: `${file.name} uploaded` });
										} catch {
											toast({ title: 'Error', description: 'Failed to upload file', variant: 'destructive' });
										}
										e.target.value = '';
									}}
								/>
							</label>
						</div>
					</div>
					<DialogFooter className="flex items-center justify-between sm:justify-between">
						<p className="text-xs text-muted-foreground">
							{selectedRegenModules.length + (regenerateCriteria ? 1 : 0)} item(s) selected
						</p>
						<div className="flex gap-2">
							<Button variant="outline" onClick={() => setRegenerateDialogOpen(false)}>
								Cancel
							</Button>
							<Button
								variant="destructive"
								disabled={isRegenerating || (selectedRegenModules.length === 0 && !regenerateCriteria)}
								onClick={handleBulkRegenerate}
							>
								{isRegenerating ? (
									<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Regenerating...</>
								) : (
									`Regenerate ${selectedRegenModules.length + (regenerateCriteria ? 1 : 0)} Item(s)`
								)}
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Share Dialog */}
			<ShareDialog
				open={shareDialogOpen}
				onOpenChange={setShareDialogOpen}
				hiringRequestId={id!}
				currentSharedTo={request.sharedTo || []}
				currentUserId={currentUser?.id || ''}
				onSuccess={() => {
					queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
				}}
			/>

			{/* Tabbed Interface */}
			<Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
				<TabsList className="w-full sm:grid sm:grid-cols-4">
					<TabsTrigger value="overview" className="flex-1 text-xs sm:text-sm px-2 sm:px-3">Overview</TabsTrigger>
					<TabsTrigger value="sourcing" className="flex-1 text-xs sm:text-sm px-2 sm:px-3">Sourcing</TabsTrigger>
					<TabsTrigger value="candidate_process" className="flex-1 text-xs sm:text-sm px-2 sm:px-3">Candidates</TabsTrigger>
					<TabsTrigger value="feedback_review" className="flex-1 text-xs sm:text-sm px-2 sm:px-3">Feedback</TabsTrigger>
				</TabsList>

				{/* Overview Tab - Includes criteria form + meeting notes */}
				<TabsContent value="overview" className="space-y-6 mt-6">

					{/* Company Research — read-only, generated at company level */}
					<Card>
						<CardHeader className="cursor-pointer" onClick={() => setIsResearchExpanded(!isResearchExpanded)}>
							<div className="flex items-center justify-between">
								<div>
									<CardTitle className="text-lg">Company Deep Research</CardTitle>
									<CardDescription>Research generated at company level</CardDescription>
								</div>
								<div className="flex items-center gap-2">
									{companyResearch?.status === 'completed' && (
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											title="Download PDF"
											onClick={async (e) => {
												e.stopPropagation();
												try {
													const { blob, filename } = await companyApi.downloadResearchPDF(companyId!);
													const url = URL.createObjectURL(blob);
													const a = document.createElement('a');
													a.href = url;
													a.download = filename || 'company-research.pdf';
													a.click();
													URL.revokeObjectURL(url);
												} catch {
													toast({ title: 'Error', description: 'PDF download failed', variant: 'destructive' });
												}
											}}
										>
											<Download className="h-4 w-4" />
										</Button>
									)}
									{companyResearch?.status === 'completed' && (
										<Badge variant="secondary" className="text-xs">
											v{companyResearch.version} &middot; Company Level
										</Badge>
									)}
									{isResearchExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
								</div>
							</div>
						</CardHeader>
						{isResearchExpanded && (
						<CardContent>
							{companyResearchLoading ? (
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<Loader2 className="h-4 w-4 animate-spin" />
									Loading company research...
								</div>
							) : !companyResearch ? (
								<p className="text-sm text-muted-foreground">No company research available. It will be auto-generated when a company is created.</p>
							) : companyResearch.status === 'pending' || companyResearch.status === 'processing' ? (
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<Loader2 className="h-4 w-4 animate-spin" />
									Company research is being generated...
								</div>
							) : companyResearch.status === 'failed' ? (
								<p className="text-sm text-destructive">Company research generation failed. You can regenerate it from the Companies page.</p>
							) : companyResearch.readableContent ? (
								<div className="prose prose-sm dark:prose-invert max-w-none prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
									<ReactMarkdown remarkPlugins={[remarkGfm]}>{companyResearch.readableContent}</ReactMarkdown>
								</div>
							) : (
								<pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-96">
									{JSON.stringify(companyResearch.content, null, 2)}
								</pre>
							)}
						</CardContent>
						)}
					</Card>

					{/* Criteria Form - Full Width on Top */}
					<Card>
						<CardHeader
							className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 space-y-0 cursor-pointer hover:bg-muted/50 transition-colors"
							onClick={() => setIsCriteriaExpanded(!isCriteriaExpanded)}
						>
							<div className="space-y-1 min-w-0">
								<CardTitle className="text-lg font-semibold flex items-center gap-2">
									Hiring Criteria
									{criteria?.version && criteria.version > 1 && (
										<Badge variant="outline" className="text-xs">v{criteria.version}</Badge>
									)}
								</CardTitle>
								<CardDescription>Define the requirements for this role</CardDescription>
							</div>
							<div className="flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
								{!isEditingCriteria && (
									<Button variant="ghost" size="icon" onClick={() => { setIsCriteriaExpanded(true); setIsEditingCriteria(true); }} title="Edit">
										<Pen className="h-4 w-4" />
									</Button>
								)}
								<Button variant="ghost" size="icon" onClick={() => { fetchCriteriaHistory(); setShowCriteriaHistory(true); }} title="History">
									<History className="h-4 w-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => {
										setIsCriteriaExpanded(true);
										setCriteriaContextText(criteria?.additionalContext || '');
										setShowCriteriaContext(true);
									}}
									disabled={generateCriteriaMutation.isPending}
									title={generateCriteriaMutation.isPending ? 'Generating...' : 'Auto-fill'}
								>
									<Sparkles className="h-4 w-4" />
								</Button>
								{isCriteriaExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground ml-1 shrink-0" /> : <ChevronDown className="h-5 w-5 text-muted-foreground ml-1 shrink-0" />}
							</div>
						</CardHeader>
						{isCriteriaExpanded && (<>
						{/* Criteria context input */}
						{showCriteriaContext && (
							<div className="px-6 pb-4 space-y-3 border-b">
								<Label>Additional Context (optional)</Label>
								<Textarea
									placeholder="e.g. Focus on leadership skills, the team is remote-first..."
									value={criteriaContextText}
									onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCriteriaContextText(e.target.value)}
									rows={3}
								/>
								<div className="flex gap-2">
									<Button
										size="sm"
										onClick={() => generateCriteriaMutation.mutate(criteriaContextText || undefined)}
										disabled={generateCriteriaMutation.isPending}
									>
										<Sparkles className="mr-2 h-3 w-3" />
										{criteriaContextText.trim() ? 'Generate with Context' : 'Generate'}
									</Button>
									<Button variant="ghost" size="sm" onClick={() => setShowCriteriaContext(false)}>
										Cancel
									</Button>
								</div>
							</div>
						)}
						<CardContent className="space-y-4 pt-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{/* Responsibilities */}
								<div>
									<Label>Responsibilities</Label>
									<div className="space-y-2 mt-2">
										{formData.responsibilities.map((item: string, i: number) => (
											<div key={i} className="flex items-center gap-2">
												<span className="flex-1 text-sm bg-muted px-3 py-1 rounded">{item}</span>
												{isEditingCriteria && (
													<Button variant="ghost" size="icon" onClick={() => handleRemoveItem('responsibilities', i)}>
														<X className="h-4 w-4" />
													</Button>
												)}
											</div>
										))}
										{formData.responsibilities.length === 0 && !isEditingCriteria && (
											<p className="text-sm text-muted-foreground">No responsibilities defined yet.</p>
										)}
										{isEditingCriteria && (
											<div className="flex gap-2">
												<Input
													placeholder="Add responsibility..."
													value={newResponsibility}
													onChange={(e) => setNewResponsibility(e.currentTarget.value)}
													onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddItem('responsibilities', newResponsibility, setNewResponsibility))}
												/>
												<Button variant="outline" size="icon" onClick={() => handleAddItem('responsibilities', newResponsibility, setNewResponsibility)}>
													<Plus className="h-4 w-4" />
												</Button>
											</div>
										)}
									</div>
								</div>

								{/* Skills */}
								<div>
									<Label>Must-Have Skills</Label>
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSkillDragEnd}>
										<SortableContext items={formData.mustHaveSkills.map((_: string, i: number) => `skill-${i}`)} strategy={rectSortingStrategy}>
											<div className="flex flex-wrap gap-2 mt-2 mb-2">
												{formData.mustHaveSkills.map((skill: string, i: number) => (
													<SortableSkill
														key={`skill-${i}`}
														id={`skill-${i}`}
														skill={skill}
														isEditing={isEditingCriteria}
														onRemove={() => handleRemoveItem('mustHaveSkills', i)}
													/>
												))}
												{formData.mustHaveSkills.length === 0 && !isEditingCriteria && (
													<p className="text-sm text-muted-foreground">No skills defined yet.</p>
												)}
											</div>
										</SortableContext>
									</DndContext>
									{isEditingCriteria && (
										<div className="flex gap-2">
											<Input
												placeholder="Add skill..."
												value={newSkill}
												onChange={(e) => setNewSkill(e.currentTarget.value)}
												onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddItem('mustHaveSkills', newSkill, setNewSkill))}
											/>
											<Button variant="outline" size="icon" onClick={() => handleAddItem('mustHaveSkills', newSkill, setNewSkill)}>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									)}
								</div>
							</div>
						</CardContent>
						{isEditingCriteria && (
							<CardFooter className="flex justify-end gap-2 border-t pt-4 mt-2">
								<Button 
									variant="outline" 
									onClick={() => {
										setFormData({
											responsibilities: criteria?.responsibilities || [],
											mustHaveSkills: criteria?.mustHaveSkills || [],
											niceToHaveSkills: criteria?.niceToHaveSkills || [],
											teamCulture: criteria?.teamCulture || [],
											successMetrics: criteria?.successMetrics || []
										});
										setIsEditingCriteria(false);
									}} 
									disabled={updateCriteriaMutation.isPending}
								>
									Cancel
								</Button>
								<Button 
									onClick={() => updateCriteriaMutation.mutate(formData)}
									disabled={updateCriteriaMutation.isPending}
								>
									<Save className="mr-2 h-4 w-4" />
									Save Criteria
								</Button>
							</CardFooter>
						)}
						</>)}
					</Card>

					{/* Module Cards for Overview Tab (Stacked below criteria) */}
					<div className="grid gap-6">
						{TAB_MODULES.overview
							.filter((config) => {
								// Only show candidate_sourcing after criteria is confirmed
								if (config.type === 'candidate_sourcing') {
									return request.status !== 'draft' && request.status !== 'gathering_criteria';
								}
								return true;
							})
							.map((config) => (
							<ModuleCard
								key={config.type}
								hiringRequestId={id!}
								moduleType={config.type}
								title={config.title}
								description={config.description}
								module={modulesByType[config.type]}
								isLoading={modulesLoading}
								onGenerate={handleGenerate}
								onSave={handleSave}
								onRefresh={refetchModules}
								{...(config.type === 'deal_briefing' ? { meetingNotes: request.meetingNotes || '' } : {})}
							/>
						))}
					</div>

					{/* Confirm Criteria Button — gates sourcing tab */}
					{request.status === 'gathering_criteria' && (
						(() => {
							const overviewTypes = ['deal_briefing', 'standard_jd', 'blind_jd'];
							const allCompleted = overviewTypes.every(t => modulesByType[t]?.status === 'completed');
							return allCompleted ? (
								<Card>
									<CardContent className="py-6 flex items-center justify-between">
										<div>
											<p className="text-sm font-medium">Ready to confirm criteria?</p>
											<p className="text-xs text-muted-foreground">Once confirmed, you can proceed to candidate sourcing.</p>
										</div>
										<Button
											onClick={async () => {
												try {
													await hiringApi.confirmCriteria(id!);
													queryClient.invalidateQueries({ queryKey: ['hiring-request', id] });
													toast({ title: 'Confirmed', description: 'Criteria confirmed. You can now generate candidate sourcing.' });
												} catch (error: any) {
													toast({ title: 'Error', description: error.message || 'Failed to confirm criteria', variant: 'destructive' });
												}
											}}
										>
											<CheckCircle className="mr-2 h-4 w-4" />
											Confirm Criteria
										</Button>
									</CardContent>
								</Card>
							) : null;
						})()
					)}

					{/* Legacy JD Links */}
					{request.jobDescriptions?.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle>Legacy Generated JDs</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{request.jobDescriptions.map((jd: any) => (
										<Link
											key={jd.id}
											to={`/job-descriptions/${jd.id}`}
											className="flex items-center gap-2 p-3 rounded border hover:bg-accent transition-colors"
										>
											<FileText className="h-4 w-4" />
											<span className="text-sm">View Job Description v{jd.currentVersionId ? '(current)' : ''}</span>
										</Link>
									))}
								</div>
							</CardContent>
						</Card>
					)}
				</TabsContent>

				{/* Sourcing Tab */}
				<TabsContent value="sourcing" className="space-y-6 mt-6">
					{request.status === 'gathering_criteria' || request.status === 'draft' ? (
						<Card>
							<CardContent className="py-12 text-center">
								<Pause className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
								<p className="text-muted-foreground">Confirm your criteria on the Overview tab before generating sourcing.</p>
								<p className="text-sm text-muted-foreground mt-1">All overview modules must be completed and confirmed first.</p>
							</CardContent>
						</Card>
					) : (
						<>
							{/* Candidate Discovery — manual trigger, versioned, chat + asset preview */}
							<CandidateDiscoveryCard hiringRequestId={id!} />

							{/* Candidate Sourcing */}
							<CandidateSearch
								hiringRequestId={id!}
								candidateSourcing={modulesByType['candidate_sourcing']?.content}
							/>
						</>
					)}
				</TabsContent>

				{/* Candidate Process Tab */}
				<TabsContent value="candidate_process" className="space-y-6 mt-6">
					{/* Candidate Ranking — manual trigger, versioned, incremental re-ranks */}
					<CandidateRankingCard hiringRequestId={id!} />

					<CandidateProcessList hiringRequestId={id!} />
				</TabsContent>

				{/* Feedback Review Tab */}
				<TabsContent value="feedback_review" className="space-y-6 mt-6">
					{/* HM Feedback Review */}
					<FeedbackReviewPanel hiringRequestId={id!} />
				</TabsContent>
			</Tabs>

			{/* Criteria History Dialog */}
			<Dialog open={showCriteriaHistory} onOpenChange={setShowCriteriaHistory}>
				<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Criteria History</DialogTitle>
						<DialogDescription>View and restore previous versions of hiring criteria</DialogDescription>
					</DialogHeader>
					{isLoadingCriteriaHistory ? (
						<div className="flex justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin" />
						</div>
					) : criteriaHistory.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-8">No history available yet.</p>
					) : (
						<div className="space-y-4">
							{criteriaHistory.map((item: any) => (
								<div key={item.id} className="border rounded-lg p-4 space-y-2">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<Badge variant="outline">v{item.version}</Badge>
											<span className="text-xs text-muted-foreground">
												{new Date(item.createdAt).toLocaleString()}
											</span>
											{item.createdBy && (
												<span className="text-xs text-muted-foreground">by {item.createdBy.name}</span>
											)}
										</div>
										{item.version !== criteria?.version && (
											<Button variant="outline" size="sm" onClick={() => handleRestoreCriteria(item.version)}>
												<RotateCcw className="mr-2 h-3 w-3" />
												Restore
											</Button>
										)}
									</div>
									{item.additionalContext && (
										<div className="bg-primary/5 rounded p-2">
											<p className="text-xs font-medium text-muted-foreground mb-1">Context:</p>
											<p className="text-sm">{item.additionalContext}</p>
										</div>
									)}
									<div className="grid grid-cols-2 gap-2 text-xs">
										{item.responsibilities?.length > 0 && (
											<div>
												<span className="font-medium">Responsibilities:</span> {item.responsibilities.length} items
											</div>
										)}
										{item.mustHaveSkills?.length > 0 && (
											<div>
												<span className="font-medium">Must-have skills:</span> {item.mustHaveSkills.length} items
											</div>
										)}
										{item.niceToHaveSkills?.length > 0 && (
											<div>
												<span className="font-medium">Nice-to-have skills:</span> {item.niceToHaveSkills.length} items
											</div>
										)}
										{item.teamCulture?.length > 0 && (
											<div>
												<span className="font-medium">Team culture:</span> {item.teamCulture.length} items
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
